import dayjs from 'dayjs'
import {
  Alert,
  Box,
  Card,
  Chip,
  Container,
  TextField,
  Typography,
} from '@mui/material'
import { ReactElement, useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  Delete,
  Edit,
  Visibility,
  Assignment,
  InfoOutlined,
  Cancel,
} from '@mui/icons-material'
import {
  DataGridPremium,
  GridColDef,
  GridActionsCellItem,
} from '@mui/x-data-grid-premium'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'
import {
  fetchScheduledAssignments,
  selectScheduledAssignmentsByService,
  selectScheduledAssignmentsStatusByService,
  selectLinkedServicesStatus,
  fetchLinkedServices,
  deleteScheduledAssignment,
  updateScheduledAssignment,
  fetchAssignmentSummariesV4,
  selectAssignmentSummariesV4Status,
} from 'src/v3/manage/manage.slice'
import type { ScheduledAssignment } from 'src/v3/core/types'
import { SearchIcon } from 'src/v3/core/icons'
import { useLinkedServiceSelection } from '../hooks/use-linked-service-selection'
import { useNotifications } from '../hooks/use-notifications'
import DeleteConfirmationModal from '../components/modals/delete-confirmation-modal'
import ConfirmationModal from '../components/modals/confirmation-modal'
import CreatedAssignmentsModal from './components/created-assignments-modal'
import { dataGridSx } from '../styles'
import {
  DATAGRID_DEFAULT_PAGE_SIZE,
  DATAGRID_PAGE_SIZE_OPTIONS,
} from '../constants'
import {
  calculateDueDaysAfterGoLive,
  normalizeRecurrence,
  isScheduleTerminated,
} from '../utils/date-utils'

export default function ScheduledAssignmentsPage(): ReactElement {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()
  let linkedServiceID = useLinkedServiceSelection()
  let { showSuccess, NotificationComponent } = useNotifications()

  let ScheduledAssignmentsToolbar = useCallback(
    () => <DataGridToolbar fileName="scheduled-assignments" />,
    [],
  )

  let [searchQuery, setSearchQuery] = useState('')
  let [scheduledAssignmentToDelete, setScheduledAssignmentToDelete] =
    useState<ScheduledAssignment | null>(null)
  let [scheduledAssignmentToCancel, setScheduledAssignmentToCancel] =
    useState<ScheduledAssignment | null>(null)
  let [createdAssignmentsModalSchedule, setCreatedAssignmentsModalSchedule] =
    useState<ScheduledAssignment | null>(null)

  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let scheduledAssignments = useAppSelector(
    selectScheduledAssignmentsByService(linkedServiceID || ''),
  )
  let scheduledAssignmentsStatus = useAppSelector(
    selectScheduledAssignmentsStatusByService(linkedServiceID || ''),
  )
  let assignmentSummariesStatus = useAppSelector(
    selectAssignmentSummariesV4Status,
  )

  useEffect(() => {
    if (linkedServicesStatus === 'uninitialized') {
      dispatch(fetchLinkedServices())
    }
  }, [dispatch, linkedServicesStatus])

  useEffect(() => {
    if (
      linkedServiceID &&
      linkedServiceID !== 'unselected' &&
      scheduledAssignmentsStatus === 'uninitialized'
    ) {
      dispatch(fetchScheduledAssignments(linkedServiceID))
    }
  }, [dispatch, linkedServiceID, scheduledAssignmentsStatus])

  useEffect(() => {
    if (assignmentSummariesStatus === 'uninitialized') {
      dispatch(fetchAssignmentSummariesV4())
    }
  }, [dispatch, assignmentSummariesStatus])

  let filteredScheduledAssignments = scheduledAssignments.filter((sa) => {
    if (!searchQuery) return true
    let query = searchQuery.toLowerCase()
    return sa.name?.toLowerCase().includes(query)
  })

  let isLoading = scheduledAssignmentsStatus === 'pending'

  let handleDelete = () => {
    if (!scheduledAssignmentToDelete) return

    dispatch(deleteScheduledAssignment(scheduledAssignmentToDelete.id))
    showSuccess('Scheduled assignment deleted successfully')
    setScheduledAssignmentToDelete(null)
  }

  let handleCancel = () => {
    if (!scheduledAssignmentToCancel) return

    let dueDaysAfterGoLive = calculateDueDaysAfterGoLive(
      scheduledAssignmentToCancel.goLiveDate,
      scheduledAssignmentToCancel.calculatedDueDate,
    )

    let recurrence = normalizeRecurrence(scheduledAssignmentToCancel.recurrence)

    let payload = {
      id: scheduledAssignmentToCancel.id,
      linkedServiceID: scheduledAssignmentToCancel.linkedServiceID,
      name: scheduledAssignmentToCancel.name,
      goLiveDate: scheduledAssignmentToCancel.goLiveDate,
      dueDaysAfterGoLive,
      userIDs: scheduledAssignmentToCancel.userIDs,
      courseIDs: scheduledAssignmentToCancel.courseIDs,
      status: 'Cancelled' as const,
      recurrence,
    }

    dispatch(updateScheduledAssignment(payload))

    if (linkedServiceID) {
      dispatch(fetchScheduledAssignments(linkedServiceID))
    }

    showSuccess('Scheduled assignment cancelled successfully')
    setScheduledAssignmentToCancel(null)
  }

  let getStatusColor = (
    status: 'Pending' | 'Activated' | 'Cancelled' | 'Complete',
  ): 'warning' | 'info' | 'success' | 'error' | 'default' => {
    switch (status) {
      case 'Pending':
        return 'warning'
      case 'Activated':
        return 'info'
      case 'Complete':
        return 'success'
      case 'Cancelled':
        return 'error'
      default:
        return 'default'
    }
  }

  let getNextGoLive = (
    scheduledAssignment: ScheduledAssignment,
  ): dayjs.Dayjs | null => {
    let goLive = dayjs(scheduledAssignment.goLiveDate)
    let today = dayjs()

    if (!scheduledAssignment.recurrence || goLive.isAfter(today)) {
      return goLive
    }

    let { recurrenceType, recurrenceInterval, recurrenceEndDate } =
      scheduledAssignment.recurrence

    // Calculate how many intervals have passed since go-live
    let unit: any = recurrenceType === 'Monthly' ? 'month' : 'year'
    let intervalsPassed = Math.floor(
      today.diff(goLive, unit) / recurrenceInterval,
    )

    // Calculate the next occurrence by adding the required number of intervals
    let nextGoLive = goLive.add(
      (intervalsPassed + 1) * recurrenceInterval,
      unit,
    )

    if (recurrenceEndDate && nextGoLive.isAfter(dayjs(recurrenceEndDate))) {
      return null
    }

    return nextGoLive
  }

  let calculateNextGoLiveDate = (
    scheduledAssignment: ScheduledAssignment,
  ): string => {
    if (isScheduleTerminated(scheduledAssignment.status)) {
      return '-'
    }

    let nextGoLive = getNextGoLive(scheduledAssignment)
    return nextGoLive ? nextGoLive.format('MMM D, YYYY') : '-'
  }

  let calculateNextDueDate = (
    scheduledAssignment: ScheduledAssignment,
  ): string => {
    if (isScheduleTerminated(scheduledAssignment.status)) {
      return '-'
    }

    let nextGoLive = getNextGoLive(scheduledAssignment)

    if (!nextGoLive) {
      return '-'
    }

    let nextDue = nextGoLive.add(scheduledAssignment.dueDaysAfterGoLive, 'day')
    return nextDue.format('MMM D, YYYY')
  }

  let columns: GridColDef<ScheduledAssignment>[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: 'nextGoLive',
      headerName: 'Next Go Live',
      width: 150,
      valueGetter: (value, row) => calculateNextGoLiveDate(row),
    },
    {
      field: 'nextDueDate',
      headerName: 'Next Due Date',
      width: 150,
      valueGetter: (value, row) => calculateNextDueDate(row),
    },
    {
      field: 'userIDs',
      headerName: 'Users',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (value, row) => row.userIDs?.length || 0,
    },
    {
      field: 'courseIDs',
      headerName: 'Courses',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (value, row) => row.courseIDs?.length || 0,
    },
    {
      field: 'recurrence',
      headerName: 'Recurrence',
      width: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          {params.value ? (
            <Chip
              label={params.value.recurrenceType}
              size="small"
              variant="outlined"
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              None
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      getActions: (params) => [
        <GridActionsCellItem
          key="view"
          icon={<Visibility />}
          label="View"
          onClick={() => navigate(`/manage/scheduled-assignments/${params.id}`)}
        />,
        <GridActionsCellItem
          key="edit"
          icon={<Edit />}
          label="Edit"
          onClick={() =>
            navigate(`/manage/scheduled-assignments/${params.id}/edit`)
          }
        />,
        <GridActionsCellItem
          key="assignments"
          icon={<Assignment />}
          label={`View Created Assignments (${params.row.createdAssignments?.length || 0})`}
          onClick={() => setCreatedAssignmentsModalSchedule(params.row)}
          showInMenu
          disabled={
            !params.row.createdAssignments ||
            params.row.createdAssignments.length === 0
          }
        />,
        <GridActionsCellItem
          key="cancel"
          icon={<Cancel />}
          label="Cancel Schedule"
          onClick={() => setScheduledAssignmentToCancel(params.row)}
          showInMenu
          disabled={
            params.row.status === 'Complete' ||
            params.row.status === 'Cancelled'
          }
        />,
        <GridActionsCellItem
          key="delete"
          icon={<Delete />}
          label="Delete"
          onClick={() => setScheduledAssignmentToDelete(params.row)}
          showInMenu
        />,
      ],
    },
  ]

  return (
    <Container maxWidth="xl">
      {NotificationComponent}
      <Box sx={{ py: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography variant="h4" component="h1">
            Scheduled Assignments
          </Typography>
        </Box>

        <Alert severity="info" icon={<InfoOutlined />} sx={{ mb: 3 }}>
          <Typography
            variant="body2"
            sx={{ mb: scheduledAssignments.length > 0 ? 2 : 0 }}
          >
            Schedules automatically create assignments on future dates. To
            create a schedule,{' '}
            <Box
              component="span"
              sx={{
                color: 'primary.main',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
              onClick={() =>
                navigate('/manage/assignments/create', {
                  state: { linkedServiceID },
                })
              }
            >
              create an assignment
            </Box>{' '}
            and choose "On a Future Date".
          </Typography>

          {scheduledAssignments.length > 0 && (
            <>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Status Guide
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 3, '& li': { mb: 0.5 } }}>
                <li>
                  <strong>Pending:</strong> Waiting for go-live date
                </li>
                <li>
                  <strong>Activated:</strong> Creating assignments on schedule
                </li>
                <li>
                  <strong>Complete:</strong> Finished creating all assignments
                </li>
                <li>
                  <strong>Cancelled:</strong> Stopped, no further assignments
                  will be created
                </li>
              </Box>
            </>
          )}
        </Alert>

        <Card sx={{ mb: 3, p: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <SearchIcon />
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Box>
        </Card>

        <Card>
          <DataGridPremium
            rows={filteredScheduledAssignments}
            columns={columns}
            slots={{ toolbar: ScheduledAssignmentsToolbar }}
            showToolbar
            loading={isLoading}
            disableRowSelectionOnClick
            pagination
            initialState={{
              pagination: {
                paginationModel: { pageSize: DATAGRID_DEFAULT_PAGE_SIZE },
              },
              pinnedColumns: { right: ['actions'] },
            }}
            pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
            sx={dataGridSx}
            autoHeight
          />
        </Card>
      </Box>
      <DeleteConfirmationModal
        isOpen={!!scheduledAssignmentToDelete}
        entityName="scheduled assignment"
        entityDisplayName={scheduledAssignmentToDelete?.name ?? ''}
        onConfirm={handleDelete}
        onClose={() => setScheduledAssignmentToDelete(null)}
      />
      <ConfirmationModal
        isOpen={!!scheduledAssignmentToCancel}
        title="Cancel Schedule"
        onConfirm={handleCancel}
        onClose={() => setScheduledAssignmentToCancel(null)}
      >
        Are you sure you want to cancel the schedule "
        {scheduledAssignmentToCancel?.name}"? This will stop the schedule from
        creating any future assignments. Assignments that have already been
        created will not be affected.
      </ConfirmationModal>
      <CreatedAssignmentsModal
        isOpen={!!createdAssignmentsModalSchedule}
        onClose={() => setCreatedAssignmentsModalSchedule(null)}
        assignmentIDs={
          createdAssignmentsModalSchedule?.createdAssignments || []
        }
        scheduledAssignmentName={createdAssignmentsModalSchedule?.name || ''}
      />
    </Container>
  )
}
